import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import styles from './styles.module.scss';

const LIST_ITEMS_GAP = 16;

const getHTMLElementsSumHeight = (elements) =>
	Array.from(elements).reduce(
		(accHeight, currentChild) =>
			accHeight + currentChild.getBoundingClientRect().height,
		0
	);

const getListMaxHeightFromObserverEntry = (entry, amountOfChildrenToShow) => {
	const itemsToShowHeights = Array.from(entry.target.children).slice(
		0,
		amountOfChildrenToShow
	);

	const itemsToShowHeight = getHTMLElementsSumHeight(itemsToShowHeights);

	const gapsHeight = LIST_ITEMS_GAP * itemsToShowHeights.length - 1;
	return itemsToShowHeight + gapsHeight;
};

const ContentList = ({ items, activeKey, itemsScrollLimit }) => {
	const [isListHidden, setIsListHidden] = useState(false);
	const [overflowEnabled, setOverflowEnabled] = useState(false);
	const [maxHeight, setMaxHeight] = useState();
	const listRef = useRef(null);
	const hideList = () => {
		setIsListHidden((prevState) => !prevState);
	};

	useEffect(() => {
		const ResizeObserverImplementation =
			'ResizeObserver' in window ? ResizeObserver : ResizeObserverPolyfill;

		const listResizeObserver = new ResizeObserverImplementation((entries) => {
			if (!isListHidden) {
				// eslint-disable-next-line no-restricted-syntax
				for (const entry of entries) {
					const itemsToCutAmount =
						items.length > itemsScrollLimit
							? items.length - itemsScrollLimit
							: 0;

					const height =
						getHTMLElementsSumHeight(entry.target.children) +
						LIST_ITEMS_GAP * (items.length - 1);

					if (itemsToCutAmount > 0) {
						const newMaxHeight = getListMaxHeightFromObserverEntry(
							entry,
							itemsScrollLimit
						);

						setMaxHeight(newMaxHeight);
						setOverflowEnabled(true);
					} else {
						setMaxHeight(height);
					}
				}
			} else {
				setMaxHeight(0);
			}
		});

		if (listRef?.current) {
			listResizeObserver.observe(listRef?.current);
		}
		return () => {
			listResizeObserver.disconnect();
		};
	}, [isListHidden]);

	const expandButtonText = isListHidden ? 'Показать' : 'Скрыть';
	const expandButtonAriaLabel = `${expandButtonText} содержание`;
	return (
		<div className={styles.ContentList}>
			<div className={styles.ContentList__Header}>
				<span className={styles.ContentList__Title}>Содержание</span>
				<button
					onClick={hideList}
					type="button"
					className={styles.ContentList__HideMark}
					aria-label={expandButtonAriaLabel}
				>
					{expandButtonText}
				</button>
			</div>
			<ol
				className={cx(styles.ContentList__List, {
					[styles.ContentList__List_collapsed]: isListHidden,
					[styles.ContentList__List_overflowAuto]:
						overflowEnabled && !isListHidden
				})}
				ref={listRef}
				style={{
					height: maxHeight,
					maxHeight
				}}
				aria-hidden={isListHidden}
				inert={isListHidden ? '' : false}
			>
				{items.map((item, index) => (
					<li
						className={cx(styles.ContentList__Link, {
							[styles.ContentList__Link_active]:
								activeKey && activeKey === item?.key
						})}
						key={item?.key || item?.title}
					>
						<button
							type="button"
							className={styles.ContentList__LinkButton}
							onClick={item.onClick}
							aria-describedby={item.ariaId}
						>
							{index + 1}. {item.title}
						</button>
					</li>
				))}
			</ol>
		</div>
	);
};

ContentList.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			onClick: PropTypes.func.isRequired,
			title: PropTypes.string.isRequired,
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		})
	).isRequired,
	activeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	itemsScrollLimit: PropTypes.number
};

ContentList.defaultProps = {
	itemsScrollLimit: 5
};

export default ContentList;
